import React from "react";
import { Link, useParams } from "react-router-dom";
import { LogIn, ShieldCheck, User } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { clearStoredAccessToken, hasStoredAccessToken } from "@/lib/authToken";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

export default function PublicAuthButton({ showMobileLabel = false }) {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  const loginLabel = t("public.common.login", { defaultValue: "Login" });
  const profileLabel = t("public.common.profile", { defaultValue: "Profile" });
  const adminLabel = t("public.common.adminPanel", { defaultValue: "Admin panel" });
  const [user, setUser] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    if (!hasStoredAccessToken()) {
      setReady(true);
      return;
    }

    base44.auth.isAuthenticated().then(async (isAuthenticated) => {
      if (isAuthenticated) {
        const me = await base44.auth.me();
        setUser(me);
      }
      setReady(true);
    }).catch(() => {
      clearStoredAccessToken();
      setReady(true);
    });
  }, []);

  if (!ready) return <div className={cn("shrink-0 rounded-full bg-muted dark:bg-white/10", showMobileLabel ? "h-9 w-20" : "h-12 w-12 md:h-10 md:w-10")} aria-hidden="true" />;

  if (!user) {
    return (
      <Button variant="outline" size="sm" onClick={() => base44.auth.redirectToLogin(window.location.href)} className={cn("shrink-0 border-slate-200 bg-white text-slate-950 shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-white dark:hover:bg-white/15", showMobileLabel ? "h-9 w-auto gap-1.5 rounded-full px-3 text-xs" : "h-12 w-12 gap-0 rounded-2xl px-0 md:h-11 md:w-auto md:gap-2 md:px-4")}>
        <LogIn className="h-4 w-4" />
        <span className={showMobileLabel ? "font-bold" : "sr-only md:not-sr-only"}>{loginLabel}</span>
      </Button>
    );
  }

  const isAdmin = user.role === "admin";
  const targetPath = isAdmin ? "/admin" : `/${lang}/profile`;
  const label = isAdmin ? adminLabel : profileLabel;

  return (
    <Link to={targetPath} className={cn("inline-flex shrink-0 items-center justify-center border border-slate-200 bg-white text-slate-950 shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-white", showMobileLabel ? "h-9 w-auto gap-1.5 rounded-full px-3 text-xs font-bold" : "h-12 w-12 overflow-hidden rounded-full md:h-10 md:w-10")} aria-label={label}>
      {isAdmin ? <ShieldCheck className="h-5 w-5 text-primary" /> : user.avatar_url ? <img src={user.avatar_url} alt="" className={showMobileLabel ? "h-5 w-5 rounded-full object-cover" : "h-full w-full object-cover"} /> : <User className="h-5 w-5 text-primary" />}
      {showMobileLabel && <span>{label}</span>}
    </Link>
  );
}